import { useState, useEffect, useCallback, useRef } from 'react';
import { CLOUD_API_URL } from '../config/serverConfig';
import { isAboveCD, getQualityTier } from '../hooks/useAudioQuality';

function normalizeId(id) {
  if (!id) return id;
  return String(id).replace(/^qobuz:(album|track|artist):/, '');
}

function getAuthToken() {
  try {
    const stored = localStorage.getItem('maestro_auth');
    return stored ? JSON.parse(stored).accessToken : null;
  } catch {}
  return null;
}

async function apiFetch(path, method = 'GET', body = null) {
  const token = getAuthToken();
  if (!token) return null;
  const opts = {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    credentials: 'omit',
  };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${CLOUD_API_URL}${path}`, opts);
  if (!res.ok) return null;
  return res.json();
}

let _favorites = new Map();
let _favListeners = new Set();
let _favLoaded = false;

function notifyFavListeners() {
  _favListeners.forEach((fn) => fn(new Map(_favorites)));
}

function _albumDedupKey(f) {
  const nAlbumId = normalizeId(f.albumId);
  const nTrackId = normalizeId(f.trackId);
  if (nAlbumId) return `aid:${nAlbumId}`;
  if (nTrackId && nAlbumId === nTrackId) return `aid:${nTrackId}`;
  const title = (f.albumTitle || f.trackTitle || '').toLowerCase().trim();
  const artist = (f.artistName || '').toLowerCase().trim();
  if (title && artist) return `ta:${title}|${artist}`;
  return `id:${nTrackId}`;
}

export async function loadFavorites() {
  // Paginate to get ALL favorites (may exceed single-page limit)
  let allFavs = [];
  let offset = 0;
  const limit = 1000;
  let hasMore = true;

  while (hasMore) {
    const data = await apiFetch(`/api/user/favorites?limit=${limit}&offset=${offset}`);
    if (!data?.favorites) break;
    allFavs = allFavs.concat(data.favorites);
    hasMore = !!data.hasMore && data.favorites.length === limit;
    offset += data.favorites.length;
  }

  if (allFavs.length === 0) return;
  _favorites.clear();

  const data = { favorites: allFavs };

  const albumSeen = new Map();
  const duplicateTrackIds = [];

  for (const f of data.favorites) {
    const dedupKey = _albumDedupKey(f);
    if (albumSeen.has(dedupKey)) {
      duplicateTrackIds.push({ trackId: f.trackId, source: f.source });
      continue;
    }
    albumSeen.set(dedupKey, f.trackId);

    const nAlbumId = normalizeId(f.albumId);
    const nTrackId = normalizeId(f.trackId || f.item_id || '');
    const itemType = nAlbumId && nTrackId === nAlbumId ? 'album' : 'track';
    const itemId = nTrackId;
    const key = `${itemType}:${itemId}`;
    _favorites.set(key, {
      item_type: itemType,
      item_id: itemId,
      source: f.source || 'maestro',
      favoritedAt: f.favoritedAt || null,
      metadata: {
        title: f.trackTitle || '',
        artistName: f.artistName || '',
        albumId: nAlbumId || null,
        albumTitle: f.albumTitle || null,
        coverUrl: f.albumArt || null,
        duration: f.duration || null,
        hires: isAboveCD(f.maxBitDepth, f.maxSampleRate),
        qualityTier: getQualityTier(f.maxBitDepth, f.maxSampleRate),
        maxBitDepth: f.maxBitDepth || null,
        maxSampleRate: f.maxSampleRate || null,
        favoritedAt: f.favoritedAt || null,
      },
    });
  }
  _favLoaded = true;
  notifyFavListeners();

  if (duplicateTrackIds.length > 0) {
    console.log('[favorites] Cleaning up', duplicateTrackIds.length, 'duplicate album entries');
    for (const dup of duplicateTrackIds) {
      const qs = dup.source ? `?source=${encodeURIComponent(dup.source)}` : '';
      apiFetch(`/api/user/favorites/${encodeURIComponent(String(dup.trackId))}${qs}`, 'DELETE').catch(() => {});
    }
  }
}

export async function toggleFavorite(itemType, itemId, source, metadata) {
  const nId = normalizeId(itemId);
  const key = `${itemType}:${nId}`;
  if (_favorites.has(key)) {
    _favorites.delete(key);
    notifyFavListeners();
    await apiFetch(`/api/user/favorites/${encodeURIComponent(String(nId))}`, 'DELETE');
  } else {
    const meta = metadata || {};
    const nAlbumId = normalizeId(meta.albumId);
    const fav = {
      item_type: itemType,
      item_id: nId,
      source: source || 'maestro',
      favoritedAt: new Date().toISOString(),
      metadata: { ...meta, albumId: nAlbumId || meta.albumId || null, favoritedAt: new Date().toISOString() },
    };
    const newMap = new Map();
    newMap.set(key, fav);
    _favorites.forEach((v, k) => { if (k !== key) newMap.set(k, v); });
    _favorites = newMap;
    notifyFavListeners();
    await apiFetch('/api/user/favorites', 'POST', {
      trackId: nId,
      trackTitle: meta.title || meta.trackTitle || '',
      artistName: meta.artistName || meta.artist || '',
      albumId: itemType === 'album' ? nId : (nAlbumId || null),
      albumTitle: meta.albumTitle || null,
      albumArt: meta.coverUrl || meta.albumArt || null,
      source: source || 'maestro',
      duration: meta.duration || null,
      hires: isAboveCD(meta.maxBitDepth, meta.maxSampleRate),
      qualityTier: getQualityTier(meta.maxBitDepth, meta.maxSampleRate),
      maxBitDepth: meta.maxBitDepth || null,
      maxSampleRate: meta.maxSampleRate || null,
    });
  }
}

export function isFavorite(itemType, itemId) {
  return _favorites.has(`${itemType}:${normalizeId(itemId)}`);
}

export function useFavorites() {
  const [favs, setFavs] = useState(() => new Map(_favorites));
  useEffect(() => {
    const handler = (m) => setFavs(m);
    _favListeners.add(handler);
    if (!_favLoaded) loadFavorites();
    return () => _favListeners.delete(handler);
  }, []);
  const toggle = useCallback((itemType, itemId, source, metadata) => toggleFavorite(itemType, itemId, source, metadata), []);
  const check = useCallback((itemType, itemId) => favs.has(`${itemType}:${normalizeId(itemId)}`), [favs]);
  const list = useCallback((itemType) => {
    const out = [];
    favs.forEach((v) => { if (!itemType || v.item_type === itemType) out.push(v); });
    return out;
  }, [favs]);
  return { favorites: favs, toggleFavorite: toggle, isFavorite: check, listFavorites: list };
}

let _playlists = [];
let _plListeners = new Set();
let _plLoaded = false;
function notifyPlListeners() { _plListeners.forEach((fn) => fn([..._playlists])); }

export async function loadPlaylists() {
  const data = await apiFetch('/api/user/playlists');
  if (!data?.playlists) return;
  _playlists = data.playlists;
  _plLoaded = true;
  notifyPlListeners();
}

export async function createPlaylist(name) {
  const data = await apiFetch('/api/user/playlists', 'POST', { name });
  if (data?.playlist) {
    _playlists = [data.playlist, ..._playlists];
    notifyPlListeners();
    return data.playlist;
  }
  return null;
}

export async function addTrackToPlaylist(playlistId, track) {
  const position = track.position || 0;
  const rawId = String(track.id || track.serviceTrackId || '').replace(/^qobuz:track:/, '');
  const isLocal = track.source === 'local_library'
    || rawId.startsWith('local:track:')
    || !!track.file_path;
  const metadata = {
    title: track.title, artistName: track.artistName || track.artist,
    duration: track.duration || 0, coverUrl: track.coverUrl || track.artworkUrl,
    albumTitle: track.albumTitle || track.album,
    albumId: track.albumId || track.album_id || null,
  };
  if (isLocal) {
    metadata.file_path = track.file_path;
    metadata.machine_id = track.machine_id;
    metadata.format = track.format;
    metadata.bit_depth = track.bit_depth;
    metadata.sample_rate = track.sample_rate;
  }
  return apiFetch(`/api/user/playlists/${encodeURIComponent(playlistId)}/tracks`, 'POST', {
    track_id: rawId,
    source: isLocal ? 'local_library' : (track.source || 'qobuz'),
    position,
    metadata,
  });
}

export function usePlaylists() {
  const [pls, setPls] = useState(() => [..._playlists]);
  useEffect(() => {
    const handler = (v) => setPls(v);
    _plListeners.add(handler);
    if (!_plLoaded) loadPlaylists();
    return () => _plListeners.delete(handler);
  }, []);
  return { playlists: pls, createPlaylist, addTrackToPlaylist, reload: loadPlaylists };
}

let _queue = [];
let _qListeners = new Set();
function notifyQListeners() { _qListeners.forEach((fn) => fn([..._queue])); }

export async function loadQueue() {
  const data = await apiFetch('/api/user/queue');
  if (!data?.queue) return;
  _queue = data.queue;
  notifyQListeners();
}

export async function addToQueue(track) {
  const position = _queue.length;
  const isLocal = track.source === 'local_library' && !!track.file_path;
  const metadata = {
    title: track.title, artistName: track.artistName || track.artist,
    duration: track.duration || 0, coverUrl: track.coverUrl || track.artworkUrl,
    albumTitle: track.albumTitle || track.album,
  };
  if (isLocal) {
    metadata.file_path = track.file_path;
    metadata.machine_id = track.machine_id;
    metadata.format = track.format;
    metadata.bit_depth = track.bit_depth;
    metadata.sample_rate = track.sample_rate;
  }
  const entry = {
    track_id: String(track.id || track.serviceTrackId || ''),
    source: track.source || 'maestro',
    position,
    metadata,
  };
  _queue = [..._queue, entry];
  notifyQListeners();
  await apiFetch('/api/user/queue', 'POST', entry);
}

export async function removeFromQueue(index) {
  const item = _queue[index];
  if (!item) return;
  _queue = _queue.filter((_, i) => i !== index);
  notifyQListeners();
  if (item.id) await apiFetch(`/api/user/queue/${encodeURIComponent(item.id)}`, 'DELETE');
}

export function useQueue() {
  const [q, setQ] = useState(() => [..._queue]);
  useEffect(() => {
    const handler = (v) => setQ(v);
    _qListeners.add(handler);
    loadQueue();
    return () => _qListeners.delete(handler);
  }, []);
  return { queue: q, addToQueue, removeFromQueue, reload: loadQueue };
}
